import React from "react";
import "./Gpmshowitworks.css";

/* ============================================================
   CONTENT  (client ka content – yahin se edit karo)
   ============================================================ */
const HEADING = {
  title: "How GPMS Works",
  subtitle: "Connecting Port Operations with Intelligent Decision-Making",
  closing:
    "From disconnected information to a connected, intelligent view of port operations.",
};

const STEPS = [
  {
    title: "Connect Existing Port Systems",
    desc: "GPMS integrates authorized operational data from Navis, CMS, gate management applications, and other port databases.",
    label: ["CONNECT", "PORT SYSTEMS"],
    icon: "link",
  },
  {
    title: "Consolidate & Analyze Data",
    desc: "Operational information is processed and organized to provide consistent dashboards, performance indicators, and analytical insights.",
    label: ["CONSOLIDATE", "& ANALYZE"],
    icon: "layers",
  },
  {
    title: "Apply AI & Computer Vision",
    desc: "GPMS Vision supports vehicle and container identification, while predictive models analyze gate congestion, truck turnaround performance, and container dwell risks.",
    label: ["AI & COMPUTER", "VISION"],
    icon: "cpu",
  },
  {
    title: "Deliver Actionable Insights",
    desc: "Relevant information is presented through intuitive operational and management dashboards, helping teams recognize potential problems and plan corrective actions.",
    label: ["ACTIONABLE", "INSIGHTS"],
    icon: "gauge",
  },
];

/* ============================================================
   DIAGRAM GEOMETRY
   ============================================================ */
const W = 760;
const H = 640;
const CX = W / 2;
const CY = H / 2;
const R = 200; // ring radius
const NODE_R = 38;
const ANGLES = [-135, -45, 45, 135]; // TL, TR, BR, BL  (clockwise flow)

const rad = (d) => (d * Math.PI) / 180;
const pt = (deg, r = R) => [CX + r * Math.cos(rad(deg)), CY + r * Math.sin(rad(deg))];

const ICONS = {
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </>
  ),
  layers: (
    <>
      <path d="m12 2 10 5-10 5L2 7z" />
      <path d="m2 17 10 5 10-5" />
      <path d="m2 12 10 5 10-5" />
    </>
  ),
  cpu: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
    </>
  ),
  gauge: (
    <>
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </>
  ),
};

/* one arc segment + arrow head between node i and node i+1 */
const buildArc = (i) => {
  const start = ANGLES[i] + 17;
  const end = (i === ANGLES.length - 1 ? ANGLES[0] + 360 : ANGLES[i + 1]) - 15;

  const [sx, sy] = pt(start);
  const baseAngle = end - 5.5;
  const [bx, by] = pt(baseAngle);
  const [tx, ty] = pt(end);
  const nx = Math.cos(rad(baseAngle));
  const ny = Math.sin(rad(baseAngle));

  return {
    d: `M ${sx} ${sy} A ${R} ${R} 0 0 1 ${bx} ${by}`,
    arrow: `${tx},${ty} ${bx + nx * 11},${by + ny * 11} ${bx - nx * 11},${by - ny * 11}`,
  };
};

const Diagram = () => (
  <svg
    className="gh-diagram"
    viewBox={`0 0 ${W} ${H}`}
    role="img"
    aria-label="GPMS four step pipeline from data to decisions"
  >
    <defs>
      <linearGradient id="gh-ring" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#1496ff" />
        <stop offset="100%" stopColor="#0a4fc4" />
      </linearGradient>
      <radialGradient id="gh-node" cx="50%" cy="40%" r="70%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#e3eefb" />
      </radialGradient>
    </defs>

    {/* ring arcs + arrows */}
    {STEPS.map((_, i) => {
      const a = buildArc(i);
      return (
        <g key={i} className="gh-ring">
          <path d={a.d} fill="none" stroke="url(#gh-ring)" strokeWidth="6" strokeLinecap="round" />
          <polygon points={a.arrow} fill="#0a78e6" />
        </g>
      );
    })}

    {/* centre badge */}
    <circle cx={CX} cy={CY} r="122" fill="rgba(255,255,255,0.14)" />
    <circle cx={CX} cy={CY} r="108" fill="#fff" />
    <text x={CX} y={CY - 48} className="gh-c-small">HOW OUR</text>
    <text x={CX} y={CY - 20} className="gh-c-small">SYSTEM WORKS</text>
    <text x={CX} y={CY + 44} className="gh-c-big">04</text>
    <text x={CX} y={CY + 82} className="gh-c-steps">STEPS</text>

    {/* nodes */}
    {STEPS.map((s, i) => {
      const [x, y] = pt(ANGLES[i]);
      const right = x > CX;
      const lx = right ? x + NODE_R + 18 : x - NODE_R - 18;
      return (
        <g key={s.title}>
          <circle cx={x} cy={y} r={NODE_R + 5} fill="rgba(255,255,255,0.18)" />
          <circle cx={x} cy={y} r={NODE_R} fill="url(#gh-node)" />
          <g
            transform={`translate(${x - 16} ${y - 16}) scale(1.34)`}
            fill="none"
            stroke="#0b6fd6"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {ICONS[s.icon]}
          </g>

          {/* number badge */}
          <circle cx={x + 28} cy={y - 28} r="13" fill="#0b4fd6" stroke="#fff" strokeWidth="2" />
          <text x={x + 28} y={y - 23} className="gh-badge">{i + 1}</text>

          {/* label */}
          <text x={lx} y={y - 4} textAnchor={right ? "start" : "end"} className="gh-label">
            {s.label[0]}
          </text>
          <text x={lx} y={y + 20} textAnchor={right ? "start" : "end"} className="gh-label">
            {s.label[1]}
          </text>
        </g>
      );
    })}
  </svg>
);

/* ============================================================
   SECTION
   ============================================================ */
const GPMSHowItWorks = () => (
  <section className="gh">
    <header className="gh__head">
      <h2 className="gh__title">{HEADING.title}</h2>
      <p className="gh__subtitle">{HEADING.subtitle}</p>
    </header>

    <div className="gh__grid">
      <div className="gh__steps">
        <ol className="gh__list">
          {STEPS.map((s, i) => (
            <li key={s.title} className="gh__item">
              <span className="gh__num">{i + 1}</span>
              <div>
                <h3 className="gh__item-title">
                  <span>Step {i + 1}:</span> {s.title}
                </h3>
                <p className="gh__item-desc">{s.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="gh__panel">
        <Diagram />
      </div>
    </div>

    <p className="gh__closing">{HEADING.closing}</p>
  </section>
);

export default GPMSHowItWorks;